// Shared Crown Points → dollar-value converter.
//
// ⚠️ CRITICAL: MUST match backend `_marginal_dollars()` in server.py
// AND the customer-facing tier structure on /crown-reserve.
//
// Feb 2026 Karine clarification (bracket #2): It's a GRADUATED /
// MARGINAL tier system, like income-tax brackets. A client with
// 2,425 pts does NOT redeem 2,425 × 6¢. They redeem:
//   • First 1,500 pts × 5¢ = $75.00
//   • Next   925 pts × 6¢ = $55.50
//   • Total                 = $130.50
//
// Brackets (all measured in LIFETIME earned points for tier name;
// applied to CURRENT BALANCE for $ value):
//   Bronze  0    – 1,500 pts  → 5¢/pt  (bracket width 1,500)
//   Silver  1,501 – 2,500 pts → 6¢/pt  (bracket width 1,000)
//   Gold    2,501 – 4,000 pts → 7¢/pt  (bracket width 1,500)
//   Diamond 4,001+           → 9¢/pt  (unbounded)

// Marginal $ value of a given balance (independent of tier name).
export const pointsToDollars = (pts) => {
  let b = Math.max(0, Math.floor(Number(pts) || 0));
  if (b <= 0) return 0;
  let d = 0;
  let take = Math.min(b, 1500); d += take * 0.05; b -= take;
  if (b > 0) { take = Math.min(b, 1000); d += take * 0.06; b -= take; }
  if (b > 0) { take = Math.min(b, 1500); d += take * 0.07; b -= take; }
  if (b > 0) { d += b * 0.09; }
  return Math.round(d * 100) / 100;
};

// Tier NAME derived from lifetime earned (not current balance).
export const tierForLifetime = (lifetimePts) => {
  const n = Number(lifetimePts) || 0;
  if (n >= 4001) return "Diamond";
  if (n >= 2501) return "Gold";
  if (n >= 1501) return "Silver";
  return "Bronze";
};

// Per-bracket breakdown for a given current-balance. Used by the
// SessionCart + admin UI to render "1,500 × 5¢ + 925 × 6¢" details
// instead of a single misleading rate.
export const bracketBreakdown = (balancePts) => {
  const b = Math.max(0, Math.floor(Number(balancePts) || 0));
  return [
    { tier: "Bronze",  rate_cents: 5, pts: Math.min(b, 1500) },
    { tier: "Silver",  rate_cents: 6, pts: Math.max(0, Math.min(b - 1500, 1000)) },
    { tier: "Gold",    rate_cents: 7, pts: Math.max(0, Math.min(b - 2500, 1500)) },
    { tier: "Diamond", rate_cents: 9, pts: Math.max(0, b - 4000) },
  ].filter(x => x.pts > 0);
};

export const formatPointsWithValue = (pts, opts = {}) => {
  const { language = 'en', separator = ' · ', showZero = true } = opts;
  const n = Math.floor(Number(pts) || 0);
  if (n <= 0 && !showZero) return '';
  const value = pointsToDollars(n);
  const valueStr = value >= 1
    ? `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : `$${value.toFixed(2)}`;
  const label = language === 'fr' ? 'valeur' : 'value';
  return `${n.toLocaleString()} pts${separator}${valueStr} ${label}`;
};

export const formatCrownValue = (pts, opts = {}) => {
  const { language = 'en' } = opts;
  const value = pointsToDollars(pts);
  const valueStr = value >= 1
    ? `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
    : `$${value.toFixed(2)}`;
  return `${valueStr} ${language === 'fr' ? 'valeur' : 'value'}`;
};

export default pointsToDollars;
